import { useCallback, useState } from "react";
import { useI18n } from "@expertloop/i18n/react";
import type { MessageKey } from "@expertloop/i18n";
import type { AnalyticsPeriodKey, AnalyticsSeriesMetric } from "@expertloop/shared";
import { overviewApi } from "../features/overview/api";
import { useAdminSection } from "../features/admin-sections/useAdminSection";
import { SectionShell } from "../features/admin-sections/SectionShell";
import { PeriodPicker } from "../features/admin-sections/PeriodPicker";
import { AttentionSection, type AttentionItem } from "../features/overview/AttentionSection";
import { ActivitySection } from "../features/overview/ActivitySection";
import { MediationsBlock, MEDIATIONS_BLOCK_SIZE } from "../features/overview/MediationsBlock";
import { FunnelSection } from "../features/overview/FunnelSection";
import { MarketplaceTiles, PopulationTiles } from "../features/overview/BusinessTiles";
import "./OverviewPage.css";

const METRICS: { key: AnalyticsSeriesMetric; labelKey: MessageKey }[] = [
	{ key: "gmv", labelKey: "adminOverview.metricGmv" },
	{ key: "platformFee", labelKey: "adminOverview.metricPlatformFee" },
	{ key: "capturedOrders", labelKey: "adminOverview.metricCapturedOrders" },
];

/** Une commande dont le remboursement est engagé mais pas encore abouti. */
const REFUND_PENDING_STATUS = "refund_pending";

/**
 * Accueil de l'administration.
 *
 * La page assemble, elle ne calcule pas : chaque chiffre est servi par la
 * fondation analytique avec sa définition, et aucune somme n'est reconstituée
 * dans le navigateur. Chaque zone se charge pour son propre compte, de sorte
 * qu'une permission manquante sur un domaine ne prive pas des autres.
 */
export function OverviewPage() {
	const { t } = useI18n();
	const [period, setPeriod] = useState<AnalyticsPeriodKey>("30d");
	const [metric, setMetric] = useState<AnalyticsSeriesMetric>("gmv");

	const users = useAdminSection(useCallback(() => overviewApi.users(period), [period]), [period]);
	const orders = useAdminSection(useCallback(() => overviewApi.orders(period), [period]), [period]);
	const marketplace = useAdminSection(
		useCallback(() => overviewApi.marketplace(period), [period]),
		[period],
	);
	const series = useAdminSection(
		useCallback(() => overviewApi.series(period, metric), [period, metric]),
		[period, metric],
	);
	const funnels = useAdminSection(useCallback(() => overviewApi.funnels(period), [period]), [period]);
	const attention = useAdminSection(useCallback(loadAttention, []), []);
	const mediations = useAdminSection(
		useCallback(() => overviewApi.pendingMediations(MEDIATIONS_BLOCK_SIZE), []),
		[],
	);

	// Les remboursements engagés se lisent dans la répartition des commandes ;
	// aucune source distincte n'existe et aucune n'est inventée.
	const refundsPending =
		orders.state.status === "ready"
			? orders.state.data.byStatusSnapshot.find((row) => row.status === REFUND_PENDING_STATUS)?.count ?? 0
			: 0;

	return (
		<div className="admin-overview">
			<div className="page-header">
				<h1>{t("adminOverview.title")}</h1>
				<p>{t("adminOverview.subtitle")}</p>
			</div>

			<MediationsBlock section={mediations} />

		<PeriodPicker value={period} onChange={setPeriod} />

			<section className="admin-overview__section">
				<h2>{t("adminOverview.attentionTitle")}</h2>
				<SectionShell section={attention}>
					{(counts) => (
						<AttentionSection items={buildAttentionItems(counts, refundsPending)} />
					)}
				</SectionShell>
			</section>

			<section className="admin-overview__section">
				<h2>{t("adminOverview.activityTitle")}</h2>
				<div className="admin-overview__metrics" role="group" aria-label={t("adminOverview.metricLabel")}>
					{METRICS.map((entry) => (
						<button
							key={entry.key}
							type="button"
							className={`admin-overview__metric ${metric === entry.key ? "is-active" : ""}`}
							aria-pressed={metric === entry.key}
							onClick={() => setMetric(entry.key)}
						>
							{t(entry.labelKey)}
						</button>
					))}
				</div>
				<SectionShell section={series}>{(data) => <ActivitySection series={data} />}</SectionShell>
			</section>

			<section className="admin-overview__section">
				<h2>{t("adminOverview.businessTitle")}</h2>
				<SectionShell section={users}>
					{(userData) => (
						<SectionShell section={orders}>
							{(orderData) => <PopulationTiles users={userData} orders={orderData} />}
						</SectionShell>
					)}
				</SectionShell>
				<SectionShell section={marketplace}>
					{(data) => <MarketplaceTiles marketplace={data} />}
				</SectionShell>
				<p className="admin-overview__notice">{t("adminOverview.marketplaceOnlyNotice")}</p>
			</section>

			<section className="admin-overview__section">
				<h2>{t("adminOverview.funnelTitle")}</h2>
				<SectionShell section={funnels}>
					{(data) => (
						<div className="admin-overview__funnels">
							<FunnelSection title={t("adminOverview.clientFunnel")} funnel={data.client} />
							<FunnelSection title={t("adminOverview.expertFunnel")} funnel={data.expert} />
							{data.invitation && <FunnelSection title={t("adminOverview.invitationFunnel")} funnel={data.invitation} />}
							{data.expertReferral && <FunnelSection title={t("adminOverview.expertReferralFunnel")} funnel={data.expertReferral} />}
						</div>
					)}
				</SectionShell>
			</section>
		</div>
	);
}

/** Décomptes des files existantes. Une file inaccessible vaut zéro et non une panne. */
async function loadAttention(): Promise<{ reports: number }> {
	const reports = await overviewApi.reportStats().then((data) => data.stats.pending).catch(() => 0);
	return { reports };
}

function buildAttentionItems(
	counts: { reports: number },
	refundsPending: number,
): AttentionItem[] {
	return [
		{
			id: "reports",
			labelKey: "adminOverview.openReports",
			count: counts.reports,
			to: "/reports",
		},
		{
			// Aucun écran d'administration ne liste ces commandes aujourd'hui :
			// la ligne informe, elle ne promet pas une action qui n'existe pas.
			id: "refunds",
			labelKey: "adminOverview.refundsPending",
			count: refundsPending,
			to: null,
			hintKey: "adminOverview.refundsPendingHint",
		},
	];
}
